import { getSupabaseClient } from './supabaseClient';
import type {
  CollaborativeGameSession,
  GameEvent,
  GameEventType,
  GameEventData,
  BuyAFeatureConfig,
} from '../types/collaborativeGame';

function generateJoinCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no 0/O/1/I to avoid confusion
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

function rowToSession(row: Record<string, unknown>): CollaborativeGameSession {
  return {
    id: row.id as string,
    toolId: row.tool_id as string,
    joinCode: row.join_code as string,
    status: row.status as CollaborativeGameSession['status'],
    hostName: (row.host_name as string) || null,
    config: row.config as BuyAFeatureConfig,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function rowToEvent(row: Record<string, unknown>): GameEvent {
  return {
    id: row.id as string,
    sessionId: row.session_id as string,
    sequenceNum: row.sequence_num as number,
    participantName: row.participant_name as string,
    eventType: row.event_type as GameEventType,
    eventData: (row.event_data as GameEventData) || {},
    createdAt: row.created_at as string,
  };
}

export async function createGameSession(
  toolId: string,
  hostName: string,
  config: BuyAFeatureConfig
): Promise<CollaborativeGameSession> {
  const supabase = getSupabaseClient();
  const joinCode = generateJoinCode();

  const { data, error } = await supabase
    .from('collaborative_game_sessions')
    .insert({ tool_id: toolId, join_code: joinCode, host_name: hostName, config })
    .select()
    .single();

  if (error) throw new Error(`Failed to create session: ${error.message}`);
  return rowToSession(data);
}

/**
 * Looks a game up by its code and records the caller as having joined it
 * (migration 997): only the host and people who joined can read a game.
 */
export async function getSessionByJoinCode(
  joinCode: string
): Promise<CollaborativeGameSession | null> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('join_game_session', {
    p_join_code: joinCode.toUpperCase().trim(),
  });

  if (error) throw new Error(`Failed to find session: ${error.message}`);
  if (!data) return null;
  return rowToSession(data as Record<string, unknown>);
}

export async function getSessionById(
  sessionId: string
): Promise<CollaborativeGameSession | null> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('collaborative_game_sessions')
    .select('*')
    .eq('id', sessionId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw new Error(`Failed to get session: ${error.message}`);
  }
  return rowToSession(data);
}

export async function updateSessionStatus(
  sessionId: string,
  status: CollaborativeGameSession['status']
): Promise<void> {
  const supabase = getSupabaseClient();

  const { error } = await supabase
    .from('collaborative_game_sessions')
    .update({ status })
    .eq('id', sessionId);

  if (error) throw new Error(`Failed to update session: ${error.message}`);
}

export async function recordEvent(
  sessionId: string,
  participantName: string,
  eventType: GameEventType,
  eventData: GameEventData = {}
): Promise<GameEvent> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('collaborative_game_events')
    .insert({ session_id: sessionId, participant_name: participantName, event_type: eventType, event_data: eventData })
    .select()
    .single();

  if (error) throw new Error(`Failed to record event: ${error.message}`);
  return rowToEvent(data);
}

export async function getSessionEvents(sessionId: string): Promise<GameEvent[]> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('collaborative_game_events')
    .select('*')
    .eq('session_id', sessionId)
    .order('sequence_num', { ascending: true });

  if (error) throw new Error(`Failed to load events: ${error.message}`);
  return (data || []).map(rowToEvent);
}
